import { useSyncExternalStore } from "react";

import { getWorkspaceConfig, subscribeWorkspaceConfigs, workspacesDelivered } from "@/data/workspaceConfig";

/** Where a workspace's Dispatch gate stands: its config still loading, or loaded with the page on or off. */
export type DispatchGate = "loading" | "enabled" | "disabled";

function dispatchGateFor(workspaceId: string | undefined): DispatchGate {
  if (!workspaceId) {
    return "disabled";
  }
  if (getWorkspaceConfig(workspaceId)?.web?.dispatch?.enabled === true) {
    return "enabled";
  }
  return workspacesDelivered() ? "disabled" : "loading";
}

/**
 * Where the Dispatch page's gate stands for a workspace: `web.dispatch.enabled` in its compiled config, or `loading`
 * until the API has delivered the user's workspaces.
 *
 * Subscribes to the config itself, not the workspace rows: a config-only change leaves the rows untouched, so every
 * reader re-renders together the moment a `/userinfo` read carries the new setting, in either direction.
 */
export function useDispatchGate(workspaceId: string | undefined): DispatchGate {
  const snapshot = () => dispatchGateFor(workspaceId);
  return useSyncExternalStore(subscribeWorkspaceConfigs, snapshot, snapshot);
}

/**
 * Whether the Dispatch page is on for a workspace: its compiled config has `web.dispatch.enabled` set.
 *
 * The one gate the route, the rail entry, and the first-location redirect share; reads `false` while the config is
 * loading (see {@link useDispatchGate}).
 */
export function useDispatchEnabled(workspaceId: string | undefined): boolean {
  return useDispatchGate(workspaceId) === "enabled";
}
